import type {
  DurationOption,
  DurationSettings,
} from "@p0security/shared/configuration/types";
import {
  timeUnitLabelOptions,
  timeUnitLabels,
} from "@p0security/shared/permission-requests/util";
import {
  Col,
  Divider,
  InputNumber,
  Row,
  Select,
  Skeleton,
  Space,
  Typography,
  message,
} from "antd";
import { useAuthFetch } from "components/Login/hook";
import { AuthzButton } from "components/common/AuthzButton";
import { useCallback, useEffect, useState } from "react";
import { handleErrorWithToastMessage } from "utils/error";

import { ErrorDisplay } from "../Error.tsx";
import { useDurationSettings } from "./useDurationSettings";

const style: React.CSSProperties = { padding: "8px 0", flex: "1  1  0px" };

export const DurationSetting: React.FC<{
  description: string;
  field: keyof Omit<DurationSettings, "options">;
  fallback: DurationOption;
  label: string;
  route: string;
}> = ({ description, fallback, field, label, route }) => {
  const [error, setError] = useState<string>();
  const [time, setTime] = useState<number | null>(1);
  const [unit, setUnit] = useState<keyof typeof timeUnitLabels>("m");

  const optionsDoc = useDurationSettings();

  useEffect(() => {
    const duration = optionsDoc.doc?.data[field] || fallback;
    if (duration) {
      setTime(duration.time);
      setUnit(duration.unit);
    }
  }, [fallback, field, optionsDoc.doc]);

  const authFetch = useAuthFetch(handleErrorWithToastMessage);

  const setDuration = useCallback(async () => {
    setError(undefined);
    if (time && unit) {
      const response = await authFetch(route, {
        json: { time, unit },
        method: "PUT",
      });
      if (!response?.ok) {
        setError(await response?.text());
        return;
      }
      message.success(`${label} set`);
    }
  }, [time, unit, authFetch, route, label]);

  return (
    <div>
      <Typography.Title level={4}>{label}</Typography.Title>
      {error && <ErrorDisplay title="Error updating duration" error={error} />}
      <div>
        <Typography.Text type="secondary">{description}</Typography.Text>
        <br />
        {optionsDoc.loading ? (
          <Skeleton.Input active block={true} />
        ) : (
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col className="gutter-row" span={24}>
              <div style={style}>
                <Typography.Text>{label}: </Typography.Text>
                <Space>
                  <Space.Compact>
                    <InputNumber min={1} value={time} onChange={setTime} />
                    <Select
                      options={timeUnitLabelOptions}
                      value={unit}
                      onChange={setUnit}
                      style={{ width: 97 }}
                    />
                  </Space.Compact>
                  <Divider />
                  <AuthzButton
                    roles={["owner"]}
                    type="primary"
                    onClick={setDuration}
                  >
                    Set
                  </AuthzButton>
                </Space>
              </div>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};
